import type { TriageChoiceOption } from '@core/entities/Triage';
import {
  TriageDeferredOptionsGatewayError,
  type TriageDeferredOptionsGateway,
  type TriageDeferredOptionsResult,
} from '@core/ports/TriageDeferredOptionsGateway';
import { AuthenticatedBffTransport } from '@infrastructure/http/AuthenticatedBffTransport';
import { BffTransportError } from '@infrastructure/http/BffTransportError';
import { hasTransientRetryStop } from '@infrastructure/http/retryOutcomeHeaders';

interface DeferredOptionsResponse {
  Items?: Array<{
    Code?: string;
    Label?: string;
    System?: string;
  }>;
  NextCursor?: string;
}

export class BffTriageDeferredOptionsHttpGateway implements TriageDeferredOptionsGateway {
  private static readonly offlineMessage = 'Your internet connection appears to be unavailable. Reconnect to the internet, then start again from the NHS App if you still need help.';

  private static readonly sessionRequiredMessage = 'Start this journey from the NHS App to continue.';

  public constructor(private readonly transport = new AuthenticatedBffTransport()) {}

  public async getOptions(sourceListUrl: string, query?: string): Promise<TriageDeferredOptionsResult> {
    const response = await this.fetchResponse(this.buildRequestUrl(sourceListUrl, query), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      if (hasTransientRetryStop(response.headers)) {
        throw new TriageDeferredOptionsGatewayError('retry-exhausted', 'Intelligent Navigator could not be reached right now.');
      }

      throw new TriageDeferredOptionsGatewayError('request-failed', 'Unable to load the list options right now.');
    }

    const payload = await response.json() as DeferredOptionsResponse;
    const items = (payload.Items ?? [])
      .filter((item) => item.Code && item.Label)
      .map((item) => ({
        code: item.Code!,
        label: item.Label!,
        system: item.System,
      } satisfies TriageChoiceOption));

    return {
      items,
      nextCursor: payload.NextCursor,
    };
  }

  private buildRequestUrl(sourceListUrl: string, query?: string): string {
    const requestUrl = new URL(sourceListUrl, window.location.origin);
    requestUrl.searchParams.set('limit', '10');

    if (query && query.trim().length > 0) {
      requestUrl.searchParams.set('query', query.trim());
    } else {
      requestUrl.searchParams.delete('query');
    }

    return `${requestUrl.pathname}${requestUrl.search}`;
  }

  private async fetchResponse(input: RequestInfo | URL, init: RequestInit): Promise<Response> {
    let response: Response;

    try {
      response = await this.transport.fetch(input, init);
    } catch (error) {
      if (error instanceof BffTransportError && error.code === 'offline') {
        throw new TriageDeferredOptionsGatewayError('offline', BffTriageDeferredOptionsHttpGateway.offlineMessage);
      }

      throw error;
    }

    if (response.status === 401) {
      throw new TriageDeferredOptionsGatewayError('session-required', BffTriageDeferredOptionsHttpGateway.sessionRequiredMessage);
    }

    return response;
  }
}